import treatmentPlanStatusConstants from '@constants/treatmentPlanStatus.constants'
import treatmentTypeMain from '@constants/treatmentTypeMain'
import useDispatchAction from '@hooks/useDispatchAction'
import AntdButton from 'components/atom/Buttons/AntdButton'
import ModalLayout from 'components/modal/ModalLayout'
import {useSelector} from 'react-redux'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {createTreatmentPlan} from 'redux/Slices/AppSlice/LeadsProfile/LeadsProfileTreatmentPlan.slice'
import {getIndividualTask} from 'redux/Slices/AppSlice/workflow/workflow.slice'
import {RootState} from 'redux/store'
import {getTreatmentPlanStatus} from '../helpers/getTreatmentPlanStatus'
import userOrderDetails from 'screens/Orders/hooks/userOrderDetails'
import {useContext} from 'react'
import {AuthContext} from 'context/AuthContext'
import useProfileBasePath from '@hooks/useProfileBasePath'

const SendForApprovalModal = ({
  refreshData,
  setShowSendForApprovalModal,
  toBeCloned,
}: {
  refreshData?: () => void
  setShowSendForApprovalModal: React.Dispatch<React.SetStateAction<boolean>>
  toBeCloned?: boolean
}) => {
  const {userId} = useContext(AuthContext)
  const {dispatchAction} = useDispatchAction()
  const [searchParams] = useSearchParams()
  const {dataLeadsOverview} = useSelector((state: RootState) => state.leadsProfile)
  const {order} = userOrderDetails(toBeCloned)
  const linkedOrderId = order?.purchase_order_details?.order_id
  const orderId = searchParams.get('order_id') ?? dataLeadsOverview?.getting_started_order_id
  const navigate = useNavigate()
  const profileBasePath = useProfileBasePath()
  const {getIndividualTaskList} = useSelector((state: RootState) => state.workFlow)
  const {serviceConfig} = useSelector((state: RootState) => state.serviceConfiguration)
  const {treatmentPlan, createTreatmentPlanLoading} = useSelector(
    (state: RootState) => state.leadsProfileTreatmentPlanReducer
  )
  const treatmentStatus = getTreatmentPlanStatus({
    treatmentPlan,
  }) as keyof typeof treatmentPlanStatusConstants
  const handleOnClick = async () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {aligner_details_meta_data, filesToSave, otherFilesToSave, video_files_to_save} =
      treatmentPlan

    dispatchAction(
      createTreatmentPlan({
        details: {
          aligner_treatment_details: aligner_details_meta_data,
          treatment_plan_id: toBeCloned ? null : treatmentPlan?.treatment_plan_id,
          treatment_sub_type: treatmentTypeMain.ALIGNERS,
          treatment_plan_tag_name: treatmentPlan.treatment_plan_tag_name,
          production_lab_details: treatmentPlan.production_lab_details,
          days_to_wear_each_aligner: treatmentPlan.days_to_wear_each_aligner,
          recommended_hours_to_wear_aligners: treatmentPlan.recommended_hours_to_wear_aligners,
          treatment_planning_software: treatmentPlan.treatment_planning_software,
          treatment_planning_link: treatmentPlan.treatment_planning_link,
          remarks: treatmentPlan.remarks,
          doctor_id: treatmentPlan.doctor_id,
          patient_id: treatmentPlan.patient_id,
          status: treatmentPlanStatusConstants.DRAFT,
          file_ids_to_clone: treatmentPlan.file_ids_to_clone,
          video_display_to_patient: treatmentPlan.is_video_display_patient,
          link_display_patient: treatmentPlan.is_link_display_patient,
          approved_by_patient_at: null,
          order_id: toBeCloned ? linkedOrderId : orderId,
          initiator_status: treatmentPlanStatusConstants.SENT_FOR_APPROVAL,
          approver_status: treatmentPlanStatusConstants.PENDING_APPROVAL,
          order_status_changed_at: new Date().toISOString(),
          ...(toBeCloned &&
            treatmentStatus !== 'DRAFT' && {
              linked_treatment_plan_id:
                treatmentPlan.treatment_plan_id ?? dataLeadsOverview?.getting_started_order_id,
            }),
          treatment_plan_upload_type: treatmentPlan.treatment_plan_upload_type,
        },
        files: filesToSave,
        other_files: otherFilesToSave,
        video_files: video_files_to_save ?? {},
        pdf_file: treatmentPlan.pdf_file_to_save,
      })
    )
      .unwrap()
      .then(() => {
        setShowSendForApprovalModal(false)
        if (serviceConfig?.PLANNING) {
          navigate(
            `${profileBasePath}/${treatmentPlan?.patient_id}/plans?order_id=${treatmentPlan.order_id}`,
            {
              replace: true,
            }
          )
        } else {
          navigate(`${profileBasePath}/${treatmentPlan?.patient_id}/plans-list`, {replace: true})
        }
        refreshData && refreshData()

        // reload workflow task so header status dropdown immediately reflects the new workflow state
        dispatchAction(
          getIndividualTask({
            doctor_id: String(userId),
            patient_id: String(treatmentPlan.patient_id),
            workflow_name: getIndividualTaskList?.workflow_name,
          } as any)
        )
      })
      .catch(() => {})
  }

  return (
    <div>
      <ModalLayout>
        <div className='flex flex-col gap-6'>
          <div className='text-center'>
            <p className='font-bold text-2xl text-black'>Submit Plan for Approval</p>
            <p className='text-textColor text-base'>
              Are you sure you want to submit this plan for approval? Once submitted, it cannot be
              edited until reviewed.
            </p>
          </div>
          <div className='flex  gap-2'>
            <button
              className='bg-white text-primaryColor border border-primaryColor h-14 font-semibold text-base w-full rounded'
              type='button'
              onClick={() => {
                setShowSendForApprovalModal(false)
              }}
            >
              Cancel
            </button>
            <AntdButton
              className='bg-primaryColor text-white h-14 font-semibold text-base w-full'
              isLoading={createTreatmentPlanLoading}
              text='Submit'
              onClick={handleOnClick}
              disabled={createTreatmentPlanLoading}
            />
          </div>
        </div>
      </ModalLayout>
    </div>
  )
}

export default SendForApprovalModal
